import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import api, { readError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(readError(err, "Could not submit the request."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Forgot password">
      {sent ? (
        <ConfirmationView
          testid="forgot-confirmation"
          title="Check your inbox"
          body="If an account exists for that email, we've sent a password reset link. Links are valid for 1 hour."
        />
      ) : (
        <form onSubmit={submit} className="space-y-6" data-testid="forgot-form">
          <p className="text-sm text-muted-foreground">
            Enter your email and we&apos;ll send you a reset link.
          </p>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              autoFocus
              value={email}
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-11 bg-card"
              data-testid="forgot-email-input"
            />
          </div>
          {error && (
            <div className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
              {error}
            </div>
          )}
          <Button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="w-full h-11 shadow-interactive">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" strokeWidth={1.5} />}
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
      <div className="mt-6 text-center text-xs text-muted-foreground">
        <Link to="/admin/login" className="hover:text-primary transition-colors">
          ← Back to login
        </Link>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen bg-background grain-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-up">
        <div className="text-center mb-8">
          <Link to="/" className="font-serif text-3xl text-foreground leading-tight">
            Tambure <span className="italic text-primary">Žmegač</span>
          </Link>
        </div>
        <Card className="bg-card/95 border-border/60 shadow-ambient">
          <CardContent className="p-8 sm:p-10">
            <h1 className="font-serif text-2xl text-foreground mb-2">{title}</h1>
            <div className="wood-divider w-16 mb-6" />
            {children}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ConfirmationView({ testid, title, body }) {
  return (
    <div data-testid={testid} className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2">
          <CheckCircle2 className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-serif text-lg text-foreground">{title}</div>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}
